import React, { useContext, useEffect, useState, useMemo, useRef } from "react"
import styled from "styled-components"
import FilterContext from "../../../context/filterContext"
import Masonry from "react-masonry-component"
import Pagination from "../../../utils/Pagination"
import { isMobile } from "../../../utils/isMobile"

import { ArticleTile } from "../Article/ArticleTile/ArticleTile"
import { useQuery } from "../../../utils/useQuery"

const Container = styled.div`
	max-width: 1120px;
	margin: auto;
	margin-top: 37px;
	@media (max-width: 767px) {
		margin-top: 47px;
	}
`

const BigImage = styled.img`
	height: 626px;
	width: 816px;
	object-fit: cover;
	@media (max-width: 767px) {
		width: 100%;
		height: 344px;
	}
`

const SecondImage = styled.img`
	height: 250px;
	width: 393px;
	object-fit: cover;
	@media (max-width: 767px) {
		width: 100%;
		height: 344px;
	}
`

const ArticleTitle = styled.h5`
	max-width: 300px;
	color: #070808;
	font-family: Mallory-Book !important;
	font-size: 18px;
	font-weight: 300;
	letter-spacing: 1px;
	line-height: 24px;
	margin-top: 0;
	margin-bottom: 22px;
	@media (max-width: 767px) {
		margin-top: 12px;
		grid-column: 2/2;
		grid-row: 1/2;
		font-family: Mallory-Book !important;
	}
`

const Tags = styled.p`
	color: #747474;
	font-family: Mallory;
	font-size: 12px;
	font-weight: 500;
	letter-spacing: 1px;
	line-height: 14px;
	margin-bottom: 0;
	margin-top: 12px;
	@media (max-width: 767px) {
		grid-column: 2/2;
		grid-row: 1/2;
		margin-top: 12px;
	}
`

const FlexTop = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr 1fr;
	@media (max-width: 767px) {
		grid-template-columns: 1fr;
		padding-inline: 12px;
	}
`

const SmallContainerLeft = styled.div`
	grid-column: 1/3;
	grid-row: 1/3;

	@media (max-width: 767px) {
		grid-column: 1/1;
		margin-bottom: 12px;
	}
`

const SmallContainerRight = styled.div`
	margin-left: 32px;
	grid-column: 3/3;
	@media (max-width: 767px) {
		grid-column: 1/1;
		margin-left: 0px;
		margin-bottom: 12px;
	}
`

// Parent container of the masonry grid
const MasonryGridContainer = styled.div`
	display: flex;
	justify-content: center;
	margin-top: 42px;
`

const ReadMore = styled.span`
	color: #777777;
	font-family: Mallory;
	font-size: 14px;
	font-weight: 300;
	letter-spacing: 0;
	line-height: 20px;
	text-decoration: underline;
	margin-bottom: 32px;
	display: block;
	@media (max-width: 767px) {
		grid-column: 2/2;
		grid-row: 1/2;
	}
`

const MobileGridContainer = styled.div`
	display: flex;
	flex-direction: column;
	padding-inline: 16px;
	margin-top: 26px;
`

const LinkWrapper = styled.a`
	text-decoration: none;
`

export const ArticleList = ({ data }) => {
	const firstFeatured = data.results[0]?.data.featured_article?.uid
	const secondFeatured = data.results[0]?.data.featured_article_2?.uid
	const thirdFeatured = data.results[0]?.data.featured_article_3?.uid

	// Masonry tile options
	const masonryOptions = {
		transitionDuration: 0,
		gutter: 32,
		isFitWidth: true,
		columnWidth: 286,
	}

	const query = useQuery()

	const filterContext = useContext(FilterContext)
	const {
		filtered,
		searchResults,
		currentPage,
		sortedResults,
		paginatedData,
		setQuery,
		totalArticles,
		setCurrentPage,
		PAGE_SIZE,
	} = filterContext

	useEffect(() => {
		setQuery(query)
	}, [query])

	const containerRef = useRef()

	const scrollToTop = (ref) => {
		ref.current.scrollIntoView({ behavior: "smooth" })
	}

	return (
		<Container ref={containerRef}>
			<FlexTop>
				{!filtered.length && !searchResults.length && (
					<>
						{sortedResults.map((article, i) => {
							if (article.uid == firstFeatured) {
								return (
									<FirstBlock
										name={article.data.title[0]?.text}
										img={article.data.featured_image.url}
										order={i}
										tag={article.data.categories[0].category.slug}
										key={article.id}
										slug={article.uid}
									/>
								)
							}

							return (
								<SecondBlock
									name={article.data.title[0]?.text}
									img={article.data.featured_image.url}
									order={i}
									tag={article.data.categories[0].category.slug}
									key={article.id}
									slug={article.uid}
									second={secondFeatured}
									third={thirdFeatured}
								/>
							)
						})}
					</>
				)}
			</FlexTop>
			{isMobile() ? (
				<MobileGridContainer>
					{paginatedData.length ? (
						paginatedData.map((article, i) => {
							return (
								<ArticleTile
									isMobile={isMobile}
									className="article-tile"
									name={article.data.title[0]?.text}
									img={article.data.featured_image.url}
									order={i}
									tag={article.data.categories[0].category.slug}
									key={article.id}
									slug={article.uid}
								/>
							)
						})
					) : (
						<></>
					)}
				</MobileGridContainer>
			) : (
				<MasonryGridContainer>
					<Masonry
						className={"my-gallery-class"}
						elementType={"div"}
						options={masonryOptions}
						disableImagesLoaded={false}
						updateOnEachImageLoad={false}
					>
						{paginatedData.length ? (
							paginatedData.map((article, i) => {
								return (
									<ArticleTile
										className="article-tile"
										name={article.data.title[0]?.text}
										img={article.data.featured_image.url}
										homeImg={article.data.homepage_image?.url}
										order={i}
										tag={article.data.categories[0].category.slug}
										key={article.id}
										slug={article.uid}
									/>
								)
							})
						) : (
							<></>
						)}
					</Masonry>
				</MasonryGridContainer>
			)}
			<Pagination
				className="pagination-bar"
				currentPage={currentPage}
				totalCount={totalArticles}
				pageSize={PAGE_SIZE}
				onPageChange={(page) => {
					setCurrentPage(page)
					scrollToTop(containerRef)
				}}
			/>
		</Container>
	)
}

function FirstBlock(props) {
	const link = `/blogs/all/${props.slug}`
	const tag = props.tag?.replace("-", " ")

	return (
		<SmallContainerLeft>
			<LinkWrapper href={link}>
				<BigImage src={props.img} />
				<Tags>{tag ? tag.toUpperCase() : ""}</Tags>
				<ArticleTitle>{props.name}</ArticleTitle>
				<ReadMore>Read more</ReadMore>
			</LinkWrapper>
		</SmallContainerLeft>
	)
}

function SecondBlock(props) {
	const link = `/blogs/all/${props.slug}`
	const tag = props.tag?.replace("-", " ")
	if (props.slug == props.second) {
		return (
			<SmallContainerRight>
				<LinkWrapper href={link}>
					<SecondImage src={props.img} />
					<Tags>{tag ? tag.toUpperCase() : ""}</Tags>
					<ArticleTitle>{props.name}</ArticleTitle>
					<ReadMore>Read more</ReadMore>
				</LinkWrapper>
			</SmallContainerRight>
		)
	} else if (props.slug == props.third) {
		return (
			<SmallContainerRight style={{ alignSelf: "end" }}>
				<LinkWrapper href={link}>
					<SecondImage src={props.img} />
					<Tags>{tag ? tag.toUpperCase() : ""}</Tags>
					<ArticleTitle>{props.name}</ArticleTitle>
					<ReadMore>Read more</ReadMore>
				</LinkWrapper>
			</SmallContainerRight>
		)
	} else {
		return false
	}
}
